import "./TestPanelListTableComponent.scss";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {ITableColumn} from "../../../../shared/models/table.model";
import {ITest} from "../../../../shared/models/test.model";
import TooltipComponent from "../../../../shared/components/tooltip/tooltipComponent";
import {CommonService} from "../../../../shared/services";
import commonService from "../../../../shared/services/common.service";
import ChipComponent from "../../../../shared/components/chip/ChipComponent";
import React, {useCallback, useState} from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {APIConfig, ImageConfig} from "../../../../constants";
import TableWrapperV2Component
    from "../../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import DrawerComponent from "../../../../shared/components/drawer/drawerComponent";
import TestPanelDetailsComponent from "../test-panel-details/TestPanelDetailsComponent";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import {getTestPanelList} from "../../../../store/actions/staticData.action";
import SwitchComponent from "../../../../shared/components/core/switch/switchComponent";

interface TestPanelListTableComponentProps {
    filterData: any;
}

const MODULE_NAME = "TestPanelList"
const TestPanelListTableComponent = (props: TestPanelListTableComponentProps) => {
    const {filterData} = props;
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const dispatch = useDispatch();
    const [isViewEditTestPanelDrawerOpen, setIsViewEditTestPanelDrawerOpen] = useState<boolean>(false);
    const [testPanelDetails, setTestPanelDetails] = useState<any>(undefined);

    const handleViewEditTestPanel = useCallback((item: any) => {
        setIsViewEditTestPanelDrawerOpen(true);
        setTestPanelDetails(item);
    }, []);

    const columns: ITableColumn[] = [
        {
            id: 'id',
            label: 'ID',
            isDefaultColumn: true,
            fixed: "left",
        },
        {
            id: 'name', label: 'Test Panel Name', isDefaultColumn: true,
            fixed: "left", width: "250",
            content: (item: ITest) => (
                <>
                    {
                        (item?.name).length > 23 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                                placement={"top"}
                                arrow={true}
                            >
                                <div className={"ellipses-for-table-data"}>
                                    {CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                                </div>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                            </>
                    }
                </>
            )
        },
        {
            id: 'code', label: 'Test Panel Code', isDefaultColumn: false,
            align: 'center',
            content: (item: ITest) => (
                <>
                    {
                        (item?.code).length > 10 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.code)}
                                placement={"top"}
                                arrow={true}
                            >
                                <div className={"ellipses-for-table-data"}>
                                    {CommonService.capitalizeFirstLetterOfEachWord(item?.code)}
                                </div>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.code)}
                            </>
                    }
                </>
            )
        },
        {
            id: 'testCount',
            label: 'Test(s)',
            isDefaultColumn: true,
            align: 'center',
            width: "100",
            content: (item: any) => (
                <>
                    {item?.testIds?.length || '-'}
                </>
            )
        },
        {
            id: 'createdAt', label: 'Added Date & Time', isDefaultColumn: false, align: 'center', width: 180,
            content: (item: ITest) => (
                <>
                    {
                        item?.createdAt ?
                            <>
                                {commonService.formatDate(item?.createdAt)}
                                &nbsp;|&nbsp;
                                {commonService.formatTime(item?.createdAt)} <br/>
                            </> : <> - </>
                    }
                </>
            )
        },
        {
            id: 'onBoardedBy', label: 'Added by', isDefaultColumn: false, align: 'center',
            content: (item: ITest) => (
                <>
                    {item?.createdByDetails ? <>{CommonService.capitalizeFirstLetterOfEachWord(item?.createdByDetails?.firstName)} {CommonService.capitalizeFirstLetterOfEachWord(item?.createdByDetails?.lastName)}</> : '-'}
                </>
            )
        },
        {
            id: 'status', label: 'Panel Status', isDefaultColumn: true, align: 'center',
            width: '150',
            content: (item: ITest) => (
                <>
                    <ChipComponent className={`min-width-80 ${item?.statusObj?.code}`}
                                   variant={'outlined'}
                                   label={CommonService.capitalizeFirstLetter(item?.statusObj?.title)}/>
                </>
            )
        },
        {
            id: 'isActive',
            label: commonService.checkAccess('Test', 'testPanelToggle', currentUser?.userAccessModules) ?
                'Active/Inactive' : '',
            width: commonService.checkAccess('Test', 'testPanelToggle', currentUser?.userAccessModules) ?
                "150" : "10",
            align: "center",
            isDefaultColumn: true,
            content: (item: ITest, index: any) => (
                <React.Fragment>
                    {
                        commonService.checkAccess('Test', 'testPanelToggle', currentUser?.userAccessModules) ?
                            <SwitchComponent
                                disabled={(item?.statusObj?.code === 'draft')}
                                size={"small"}
                                checked={item.isActive}
                                onUpdate={() => handleTestPanelToggle(item, index)}/> :
                            <></>}
                </React.Fragment>
            )
        },
        {
            id: 'actions',
            label: '',
            isDefaultColumn: true,
            align: 'center',
            fixed: "right",
            width: "100",
            hideInColumnPreferences: true,
            content: (item: ITest, index: any) => (
                <React.Fragment>
                    <div className="table-actions" id={`view_testPanel_` + index}>
                        <div className="table-action" onClick={() => {
                            handleViewEditTestPanel(item)
                        }}>
                            <ChevronRightIcon/>
                        </div>
                    </div>
                </React.Fragment>
            )
        },
    ];

    const refreshTable = useCallback(() => {
        CommonService._communications.TableWrapperRefreshSubject.next({
            moduleName: MODULE_NAME
        });
    }, []);

    const onToggleTestPanel = useCallback((item: any, index: any) => {
        CommonService._test.toggleTestPanel(item.id, {})
            .then((response: IAPIResponseType<any>) => {
                refreshTable();
                dispatch(getTestPanelList())
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.error || `Error while ${item?.isActive ? "deactivating" : "activating"} the test panel`, "error");
            });
    }, [dispatch,refreshTable]);

    const handleTestPanelToggle = useCallback((item: any, index: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: `Are you sure you want to ${item?.isActive ? "deactivate" : "activate"} this test panel?`,
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: `${item?.isActive ? "Deactivate" : "Activate"}`,
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        })
            .then(() => {
                onToggleTestPanel(item, index);
            })
            .catch(() => {
            })
    }, [onToggleTestPanel]);

    return (
        <div className={'test-panel-list-table-component'}>
            <TableWrapperV2Component
                isPaginated={true}
                columns={columns}
                url={APIConfig.GET_TEST_PANEL_LIST.URL}
                method={APIConfig.GET_TEST_PANEL_LIST.METHOD}
                extraPayload={filterData}
                isColumnFilterVisible={true}
                moduleName={MODULE_NAME}
            />
            <DrawerComponent
                open={isViewEditTestPanelDrawerOpen}
                closeOnBackDropClick={true}
                closeOnEsc={true}
                className={"drawer-wrapper"}
                onClose={() => setIsViewEditTestPanelDrawerOpen(false)}
            >
                <TestPanelDetailsComponent testPanelDetails={testPanelDetails}
                                           onSaved={()=>{
                                               setIsViewEditTestPanelDrawerOpen(false);
                                               refreshTable()
                                           }}/>
            </DrawerComponent>
        </div>
    );

};

export default TestPanelListTableComponent;